import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SpinButtonHandler } from '../utils/spin-button-handler.utils';
import { SpinnerState, ButtonState, SPINNER_STATE } from '../interfaces/button-spinner.interface';

/**
 * Service to manage button loading states and spinners
 */
@Injectable({
  providedIn: 'root'
})
export class SpinButtonService {
  private readonly DEFAULT_STATE: ButtonState = { loading: false };
  private readonly RESET_DELAY = 1500; // 1.5 seconds
  private buttonStates = new Map<string, BehaviorSubject<ButtonState>>();
  private buttonHandlers = new Map<string, SpinButtonHandler>(); // Store handlers
  private renderer: Renderer2;

  constructor(private rendererFactory: RendererFactory2) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  /**
   * Handle start spinner from window event
   */
  handleStartSpinner(instance: any): void {
    if (!instance?.id || !instance?.element) return;

    const instanceId = instance.id;

    // Create handler if doesn't exist
    if (!this.buttonHandlers.has(instanceId)) {
      const handler = new SpinButtonHandler(
        instance.element,
        this.renderer,
        this,
        instanceId
      ).initialize();

      this.buttonHandlers.set(instanceId, handler);
    }

    // Get the actual button element
    const button = instance?.element?.querySelector('button');
    if (button) {
      // Set processing state immediately (this should already be set by interceptor, but as backup)
      button.setAttribute('data-submitting', 'true');
    }

    // Start spinner
    this.startSpinner(instanceId);
  }

  /**
   * Handle stop spinner from window event
   */
  handleStopSpinner(instance: any, state: SpinnerState = SPINNER_STATE.RESET): void {
    if (!instance?.id) return;

    const instanceId = instance.id;

    // Remove data-submitting attribute
    const button = instance?.element?.querySelector('button');
    if (button) {
      button.removeAttribute('data-submitting');
    }

    this.stopSpinner(instanceId, state);

    // If success/error: cleanup after delay to show icon
    // If reset: immediate cleanup
    if (state === SPINNER_STATE.SUCCESS || state === SPINNER_STATE.ERROR) {
      setTimeout(() => {
        this.cleanupHandler(instanceId);
      }, this.RESET_DELAY);
    } else {
      this.cleanupHandler(instanceId);
    }
  }

  /**
   * Cleanup button handler
   */
  private cleanupHandler(instanceId: string): void {
    const handler = this.buttonHandlers.get(instanceId);
    if (handler) {
      handler.destroy();
      this.buttonHandlers.delete(instanceId);
    }
  }

  /**
   * Get or create button state subject
   */
  private getButtonSubject(buttonKey: string): BehaviorSubject<ButtonState> {
    let subject = this.buttonStates.get(buttonKey);
    
    if (!subject) {
      subject = new BehaviorSubject<ButtonState>(this.DEFAULT_STATE);
      this.buttonStates.set(buttonKey, subject);
    }

    return subject;
  }

  /**
   * Start loading state for button
   */
  startSpinner(buttonKey: string): void {
    this.getButtonSubject(buttonKey).next({ loading: true });
  }

  /**
   * Stop loading and set final state
   */
  stopSpinner(buttonKey: string, state: SpinnerState = 'reset'): void {
    const newState: ButtonState = { loading: false };
    
    // Set success/error state if specified
    if (state === SPINNER_STATE.SUCCESS) newState.success = true;
    if (state === SPINNER_STATE.ERROR) newState.error = true;
    
    const subject = this.getButtonSubject(buttonKey);
    subject.next(newState);
  }

  /**
   * Get button state as observable
   */
  getState(buttonKey: string): Observable<ButtonState> {
    return this.getButtonSubject(buttonKey).asObservable();
  }

  /**
   * Clear button state
   */
  clear(buttonKey: string): void {
    const subject = this.buttonStates.get(buttonKey);
    if (subject) {
      subject.complete();
      this.buttonStates.delete(buttonKey);
    }
  }
}